import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboard } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const PALETTE = ["#0ea5a3", "#2563eb", "#f59e0b", "#8b5cf6", "#ef4444", "#10b981", "#ec4899", "#6b7280", "#14b8a6", "#f97316"];

// Biểu đồ cột ngang đơn giản (không cần thư viện): [ [label, value], ... ]
function BarChart({ title, data, colorAt }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div style={cardStyle}>
      <h3 style={chartTitleStyle}>{title}</h3>
      {entries.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>Chưa có dữ liệu.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.map(([label, value], i) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 130, fontSize: 12, color: "#4b5563", textAlign: "right", flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={label}>
                {label}
              </div>
              <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 6, height: 20, position: "relative" }}>
                <div style={{ width: `${(value / max) * 100}%`, minWidth: 2, height: "100%", borderRadius: 6, background: (colorAt || ((idx) => PALETTE[idx % PALETTE.length]))(i) }} />
              </div>
              <div style={{ width: 48, fontSize: 13, fontWeight: 700, color: "#111827", textAlign: "right", flexShrink: 0 }}>{value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...cardStyle, flex: "1 1 170px", minWidth: 160 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: color || "#111827", lineHeight: 1.1, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 13, color: "#4b5563", marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const HappySchoolDashboard = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    getDashboard(schoolId)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (!schoolId) {
    return (
      <div style={{ padding: 24 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const khoiLabel = (data2, key) => {
    const obj = {};
    ["12", "11", "10"].forEach((k) => { if (data2[k] !== undefined) obj[`Khối ${k}`] = data2[k]; });
    Object.entries(data2).forEach(([k, v]) => { if (!["12", "11", "10"].includes(k)) obj[key === "khoi" ? `Khối ${k}` : k] = v; });
    return obj;
  };

  const classesWithStudents = data ? Object.values(data.students.byClass || {}).filter((n) => n > 0).length : 0;
  const avgClassSize = data && classesWithStudents ? Math.round(data.students.total / classesWithStudents) : 0;
  const hsPerGv = data && data.staff.teachers ? Math.round(data.students.total / data.staff.teachers) : 0;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto", textAlign: "left" }}>
      <h2 style={{ margin: "0 0 4px 0" }}>📊 Bảng điều hành — Hiệu trưởng</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>Tổng quan số liệu toàn trường.</p>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : !data ? null : (
        <>
          {/* KPI */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <Kpi icon="🧑‍🎓" label="Học sinh" value={data.students.total} color="#0ea5a3" />
            <Kpi icon="🏫" label="Lớp học" value={data.classes.total} color="#2563eb" />
            <Kpi icon="🗂️" label="Nhân sự" value={data.staff.total} color="#8b5cf6" />
            <Kpi icon="👩‍🏫" label="Giáo viên" value={data.staff.teachers} sub="THPT + Nghề" color="#f59e0b" />
            <Kpi icon="📐" label="Sĩ số TB/lớp" value={avgClassSize} color="#10b981" />
            <Kpi icon="⚖️" label="HS / Giáo viên" value={hsPerGv} color="#ec4899" />
            <Kpi
              icon="📸"
              label="HS đã đăng ký điểm danh AI"
              value={data.students.aiAttendance || 0}
              sub={data.students.total ? `${Math.round(((data.students.aiAttendance || 0) / data.students.total) * 100)}% tổng HS` : ""}
              color="#0ea5a3"
            />
          </div>

          {/* Charts */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            <BarChart title="Học sinh theo Khối" data={khoiLabel(data.students.byKhoi, "khoi")} />
            <BarChart title="Nhân sự theo Khối / Phòng ban" data={data.staff.byDept} />
            <BarChart title="Lớp theo Tổ hợp" data={data.classes.byCombo} />
            <BarChart title="Lớp theo Khối" data={khoiLabel(data.classes.byKhoi, "khoi")} />
            <BarChart title="Nhân sự theo trạng thái" data={data.staff.byStatus} />
            <BarChart title="Giới tính học sinh" data={data.students.byGender} />
          </div>

          {/* Ban giám hiệu */}
          {data.leadership && data.leadership.length > 0 && (
            <div style={{ ...cardStyle, marginTop: 16 }}>
              <h3 style={chartTitleStyle}>Ban giám hiệu</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {data.leadership.map((p) => (
                  <div key={p.name} style={{ padding: "8px 14px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8 }}>
                    <div style={{ fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#0f766e" }}>{(p.roles || []).join(", ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, boxShadow: "0 1px 3px rgba(0,0,0,.04)" };
const chartTitleStyle = { margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: "#374151" };

export default HappySchoolDashboard;
